<template>
  <div class="cms-html-wrapper" v-html="value" />
</template>
<script lang="ts" setup>
  const props = defineProps({
    value: {
      type: String,
      required: true,
    },
  })

  const route = useRoute()

  // it's required because html content will be rendered after the page is loaded, so native hash link won't work
  watch(
    () => props.value,
    (val) => {
      if (!val) return
      const hashtag = location.hash.substring(1)
      if (!hashtag) return
      setTimeout(() => {
        const hashtagTargetDom = document.body.querySelector(`a[name="${hashtag}"]`) ?? document.getElementById(hashtag)
        hashtagTargetDom?.scrollIntoView()
      }, 50)
    },
    { immediate: true }
  )

  // only for legacy hash link
  watch(
    () => route.hash,
    (val) => {
      const hashtag = val.substring(1)
      const hashtagTargetDom = document.body.querySelector(`a[name="${hashtag}"]`)
      if (hashtagTargetDom) hashtagTargetDom.scrollIntoView({ behavior: 'smooth' })
    }
  )
</script>
<style lang="scss" scoped>
  .cms-html-wrapper {
    max-width: 1100px;
    margin: 0 auto;
    :deep() {
      // ------ General Styling for the Post
      font-size: 17px;
      line-height: 1.5em;
      color: #1a1a1a;
      * {
        text-decoration-color: var(--accent-color1) !important;
        text-decoration-thickness: 2px !important;
        text-underline-offset: 3px;
      }
      img {
        max-width: 100%;
      }
      .notice {
        color: var(--accent-color1);
      }
      h1 {
        font-size: 30px;
      }
      h2 {
        font-size: 28px;
      }
      h3 {
        font-size: 26px;
      }
      h4 {
        font-size: 24px;
      }
      h5 {
        font-size: 22px;
      }
      h1,
      h2,
      h3,
      h4,
      h5,
      h6 {
        line-height: 1em;
      }
      p {
        margin-bottom: 15px;
      }
      a {
        color: var(--accent-color1);
        font-weight: bold;
        text-decoration: underline;
        transition: all 0.3s;
        &:hover {
          color: var(--accent-color2);
          text-decoration-color: var(--accent-color2) !important;
        }
      }
      // anchor hash link
      a[name],
      *[id] {
        scroll-margin-top: 170px;
      }
      img {
        max-width: 100%;
      }
      dl,
      ul,
      ol,
      table {
        margin-bottom: 20px;
      }
      dl,
      ul,
      ol {
        padding-left: 20px;
        li {
          margin-bottom: 6px;
          &::marker {
            color: var(--accent-news-color1);
          }
        }
        ol {
          list-style-type: lower-alpha;
          margin-top: 5px;
          margin-bottom: 10px;
          ol {
            list-style-type: lower-roman;
          }
        }
        dl,
        ul {
          margin-top: 5px;
          margin-bottom: 10px;
          li {
            position: relative;
            &::marker {
              list-style-type: none;
              content: '';
            }
            &:before {
              content: '';
              border-radius: 3px;
              width: 8px;
              height: 4px;
              background: var(--accent-news-color1);
              position: absolute;
              left: -18px;
              top: 10px;
            }
            dl,
            ul {
              li {
                &::marker {
                  list-style-type: none;
                  content: '';
                }
                &:before {
                  background: grey;
                }
              }
            }
          }
        }
      }
      table {
        width: 100%;
        height: auto;
        border: none;
        border-collapse: collapse;
        background: #f5f5f5;
        /* border-top-left-radius: 10px; */

        display: block;
        max-width: -moz-fit-content;
        max-width: fit-content;
        overflow-x: auto;
        tr:first-child {
          background: var(--accent-news-color1);
          color: white !important;
          th,
          td,
          li,
          span {
            color: white !important;
          }
        }
        tr {
          th,
          td {
            padding: 8px 9px;
            border: 1px solid #cccccc;
          }
        }
      }
      b,
      strong {
        font-weight: bold;
      }

      iframe {
        max-width: 850px;
        width: 100%;
        display: block;
        margin: 0 auto;
        height: auto;
        aspect-ratio: 16 / 9;
      }

      .play-btn {
        cursor: pointer;
      }
    }
  }

  @media #{$tablet} {
    .cms-html-wrapper {
      :deep() {
        .hide-mobile {
          display: none !important;
        }
      }
    }
  }

  @media #{$pcOnly} {
    .cms-html-wrapper {
      :deep() {
        .hide-desktop {
          display: none !important;
        }
      }
    }
  }
</style>
